import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import { SingleDateTimeFormField } from '~components/FormFields/SingleDateTimeFormField';

import type { ProjectFormSchemaInterface } from '../../projectFormSchema';

// Mirrors the legacy form's date/time section. Render-only by design.
//
// Regression gate: the schema keys are `effectiveAt` and `expiresAt`
// (camelCase). The deparser map translates these to BE-shape `effective_at` /
// `expires_at`. A rename to `startDate` / `endDate` here reintroduces the
// 05-20-26 date-loss regression. The pair of constants below pins both the
// wrapper `name` attribute (read by the regression test) and the
// SingleDateTimeFormField `name` prop (read by react-hook-form) to the same
// `keyof ProjectFormSchemaInterface` value — drift between them is a compile
// error.
const EFFECTIVE_AT: keyof ProjectFormSchemaInterface = 'effectiveAt';
const EXPIRES_AT: keyof ProjectFormSchemaInterface = 'expiresAt';

export function DateAndTimePanel(): JSX.Element {
  const { control, formState } = useFormContext<ProjectFormSchemaInterface>();

  return (
    <Card sx={{ p: 2, mb: 2 }}>
      <Typography variant="form-section-title" sx={{ mb: 1 }}>
        {t('form_fields.date_and_time')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <div data-test-id="project-form-v2-effective-at" {...{ name: EFFECTIVE_AT }}>
          <SingleDateTimeFormField
            clearOnNull
            control={control}
            dateLabel={t('project.form.effective_at_date')}
            errors={formState.errors}
            isRequired={false}
            name={EFFECTIVE_AT}
            timeLabel={t('project.form.effective_at_time')}
          />
        </div>

        <div data-test-id="project-form-v2-expires-at" {...{ name: EXPIRES_AT }}>
          <SingleDateTimeFormField
            clearOnNull
            control={control}
            dateLabel={t('project.form.expires_at_date')}
            errors={formState.errors}
            isRequired={false}
            name={EXPIRES_AT}
            timeLabel={t('project.form.expires_at_time')}
          />
        </div>
      </Box>
    </Card>
  );
}
